import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { User, Lock, Save } from 'lucide-react'
import { toast } from 'react-toastify'
import { usersAPI, authAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'

function Profile() {
  const [activeTab, setActiveTab] = useState('profile')
  const { user, loadUser } = useAuth()
  const queryClient = useQueryClient()

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors },
  } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || ''
    }
  })

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm()

  const updateProfileMutation = useMutation(usersAPI.updateProfile, {
    onSuccess: () => {
      toast.success('Profile updated successfully')
      loadUser()
      queryClient.invalidateQueries('users')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update profile')
    }
  })

  const changePasswordMutation = useMutation(authAPI.changePassword, {
    onSuccess: () => {
      toast.success('Password changed successfully')
      resetPassword()
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to change password')
    }
  })

  const onSubmitProfile = (data) => {
    updateProfileMutation.mutate(data)
  }

  const onSubmitPassword = (data) => {
    changePasswordMutation.mutate(data)
  }

  const tabs = [
    { id: 'profile', name: 'Profile Information', icon: User },
    { id: 'password', name: 'Change Password', icon: Lock },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-primary-900">Profile Settings</h1>
        <p className="mt-1 text-sm text-primary-600">
          Manage your account information and security settings
        </p>
      </div>

      {/* User Info Card */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 rounded-full bg-primary-900 flex items-center justify-center">
              <span className="text-xl font-bold text-white">
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-primary-900">
                {user?.firstName} {user?.lastName}
              </h3>
              <p className="text-sm text-primary-500">{user?.email}</p>
              <p className="text-sm text-primary-500">
                <span className={`status-badge ${user?.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                  {user?.role}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-primary-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-primary-500 hover:text-primary-700 hover:border-primary-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              )
            })}
          </nav>
        </div>

        <div className="card-body">
          {activeTab === 'profile' && (
            <form onSubmit={handleSubmitProfile(onSubmitProfile)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    First Name
                  </label>
                  <input
                    {...registerProfile('firstName', { required: 'First name is required' })}
                    type="text"
                    className="input-field"
                  />
                  {profileErrors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{profileErrors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    Last Name
                  </label>
                  <input
                    {...registerProfile('lastName', { required: 'Last name is required' })}
                    type="text"
                    className="input-field"
                  />
                  {profileErrors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{profileErrors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-700 mb-1">
                  Email Address
                </label>
                <input
                  {...registerProfile('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  className="input-field"
                />
                {profileErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{profileErrors.email.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={updateProfileMutation.isLoading}
                  className="btn-primary"
                >
                  {updateProfileMutation.isLoading && <LoadingSpinner size="sm" className="mr-2" />}
                  <Save className="h-4 w-4 mr-2" />
                  {updateProfileMutation.isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'password' && (
            <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-1">
                  Current Password
                </label>
                <input
                  {...registerPassword('currentPassword', { required: 'Current password is required' })}
                  type="password"
                  className="input-field"
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-700 mb-1">
                  New Password
                </label>
                <input
                  {...registerPassword('newPassword', { 
                    required: 'New password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  type="password"
                  className="input-field"
                />
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  {...registerPassword('confirmPassword', { 
                    required: 'Please confirm your new password',
                    validate: (value, formValues) => 
                      value === formValues.newPassword || 'Passwords do not match'
                  })}
                  type="password"
                  className="input-field"
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={changePasswordMutation.isLoading}
                  className="btn-primary"
                >
                  {changePasswordMutation.isLoading && <LoadingSpinner size="sm" className="mr-2" />}
                  <Lock className="h-4 w-4 mr-2" />
                  {changePasswordMutation.isLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default Profile